import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AxiosClient from '../../assets/AxiosClient';
import { DEFAULT_CONFERENCE_ID, useStateContext } from '../../Contexts/ContextProvider';
import ToggleSwitch from '../../Models/ToggleSwitch';

const EditSection = () => {
  const { SetNotification } = useStateContext();
  const { id } = useParams(); // Get the section id from the URL params
  const titleRef = useRef(null);
  const descriptionRef = useRef(null);
  const [image_, setImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [lang, setLang] = useState('English');
  const [url, setUrl] = useState('');
  const [conferenceId, setConferenceId] = useState(DEFAULT_CONFERENCE_ID);
  const [sending, setIsSending] = useState(false);
  const [isUrlActive, setIsUrlActive] = useState(false); // Toggle state for URL
  const [isImageActive, setIsImageActive] = useState(false); // Toggle state for Image
  const navigate = useNavigate();
  const {API} = useStateContext() ; 
  // Fetch the section data on component mount
  useEffect(() => {
    const fetchSectionData = async () => {
      try {
        const response = await AxiosClient.get(`/sections/${id}`);
        const sectionData = response.data;

        // Pre-fill the form with existing section data
        titleRef.current.value = sectionData.title;
        descriptionRef.current.value = sectionData.description;
        setLang(sectionData.language);
        setUrl(sectionData.url);
        setImagePreviewUrl(sectionData.image ? API + sectionData.image : null);
        setConferenceId(sectionData.conference_id);

        // Toggle states based on existing data
        if (sectionData.image) setIsImageActive(true);
        if (sectionData.url) setIsUrlActive(true);
      } catch (error) {
        SetNotification('Error fetching section data', 'danger');
      }
    };

    fetchSectionData();
  }, [id, SetNotification]);

  // Remove the stored value after turning off the button
  useEffect(() => {
    if (!isUrlActive) {
      setUrl(null);
    }
    if (!isImageActive) {
      setImage(null);
    }
  }, [isImageActive, isUrlActive]);

  const handleImageChange = (e) => {
    const selectedImage = e.target.files[0];
    setImage(selectedImage);

    const reader = new FileReader();
    reader.onload = (event) => {
      setImagePreviewUrl(event.target.result);
    };
    reader.readAsDataURL(selectedImage);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSending(true);

    const formData = new FormData();
    formData.append('title', titleRef.current.value || '');
    formData.append('language', lang);
    formData.append('description', descriptionRef.current.value || '');
    if (image_) formData.append('image', image_);
    formData.append('url', url || '');
    formData.append('conference_id', conferenceId || '');

    try {
      const response = await AxiosClient.put(`/sections/${id}`, formData);
      if (response.status === 200) {
        SetNotification('Section updated successfully', 'success');
        navigate(-1);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An error occurred while updating the section.';
      SetNotification(errorMessage, 'danger');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="m-4">
      <div className="d-flex">
        <label className="h6 mb-0 h6 m-3">
          Language
        </label>

        <button
          className={`btn btn-${lang === 'Arabic' ? 'info' : 'dark'} m-2`}
          onClick={() => setLang('Arabic')}
        >
          Arabic
        </button>
        <button
          className={`btn btn-${lang === 'English' ? 'info' : 'dark'} m-2`}
          onClick={() => setLang('English')}
        >
          English
        </button>
        {lang === 'Arabic' ? (
          <img src="https://flagsapi.com/IQ/shiny/64.png" alt="Arabic" />
        ) : (
          <img src="https://flagsapi.com/GB/shiny/64.png" alt="English" />
        )}

        <ToggleSwitch message="Add Image" state={isImageActive} updateIsActive={setIsImageActive} />
        <ToggleSwitch message="Add URL Button" state={isUrlActive} updateIsActive={setIsUrlActive} />
      </div>
      <form className="form-group" dir={lang === 'Arabic' ? 'rtl' : 'ltr'} onSubmit={handleSubmit}>
        <h5>{lang === 'English' ? 'Title' : 'عنوان القسم'}</h5>
        <div className="input-group input-group-outline mb-3">
          <input type="text" className="form-control" placeholder="Enter title" ref={titleRef} />
        </div>
        <h5>{lang === 'English' ? 'Description' : 'وصف القسم'}</h5>
        <div className="input-group input-group-outline mb-3">
          <textarea
            className="form-control"
            placeholder="Enter description"
            rows={5}
            ref={descriptionRef}
          ></textarea>
        </div>

        {isUrlActive && (
          <div>
            <h5>{lang === 'English' ? 'URL' : 'الرابط'}</h5>
            <div className="input-group input-group-outline mb-3">
              <input
                type="url"
                className="form-control"
                placeholder="Enter URL"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
            </div>
          </div>
        )}

        {isImageActive && (
          <div>
            <h5>{lang === 'English' ? 'Image' : 'صورة القسم'}</h5>
            <div className="input-group input-group-outline mb-3">
              <input type="file" accept="image/*" onChange={handleImageChange} className="form-control" />
             
            </div>
            {imagePreviewUrl && (
                <img
                  src={imagePreviewUrl}
                  alt="Preview"
                  style={{ maxWidth: '250px', maxHeight: '25%' }}
                  className="mt-2"
                />
              )}
          </div>
        )}

        <hr className="my-4" />
        <button type="submit" className="btn btn-primary" disabled={sending}>
          {sending ? 'Submitting...' : 'Submit'}
        </button>
      </form>
    </div>
  );
};

export default EditSection;
